import React, { Component } from "react";
import { Button } from "react-bootstrap";
import { columnKey, TABLE_COLUMN_TYPES, TABLE_MAX_COLUMNS, TABLE_DEFAULT_ROWS } from "./broadcastFormHelpers";

/**
 * The Questions section of the publisher form (kind = form). Presentational: `questions` is the
 * Formik value [{qtype, label, help_text, required, options}] in order and every edit calls
 * onChange with a new array. Position is the array order (SPEC 19.1), so there is no position
 * field here, only Move up / Move down.
 *
 * Options for single / multiple choice are typed one per line; the lines (trimmed, blanks
 * dropped) become `options`. The raw text is kept locally while typing so a trailing newline
 * is not swallowed by the controlled textarea.
 *
 * SPEC 23.2: a "Table" question carries `columns` [{key, label, type}] and `max_rows`; the key is
 * made from the label (columnKey) when the label is typed and stays as the server stored it
 * otherwise. With `perPerson` each question also gets "Filled in by" (employee | hr).
 */

export const QUESTION_TYPES = [
  ["short_text", "Short text"],
  ["long_text", "Long text"],
  ["rating", "Rating 1-5"],
  ["single", "Single choice"],
  ["multi", "Multiple choice"],
  ["yes_no", "Yes or No"],
  ["date", "Date"],
  ["table", "Table"],
];

export const COLUMN_TYPE_LABELS = { text: "Text", number: "Number", date: "Date" };
export const FILLED_BY = [["employee", "Employee"], ["hr", "HR"]];

export const newColumn = (taken) => ({ key: columnKey("", taken), label: "", type: "text" });

export const MAX_QUESTIONS = 40;
export const CHOICE_TYPES = ["single", "multi"];

export const newQuestion = () => ({ qtype: "short_text", label: "", help_text: "", required: true, options: [] });

export const parseOptions = (text) => String(text || "").split("\n").map((line) => line.trim()).filter((line) => line !== "");

class QuestionBuilder extends Component {
  state = {
    drafts: {}, // index -> raw options text while it is being typed
  };

  list = () => (Array.isArray(this.props.questions) ? this.props.questions : []);

  emit = (next, drafts) => {
    if (drafts !== undefined) this.setState({ drafts: drafts });
    this.props.onChange(next);
  };

  add = () => this.emit(this.list().concat([newQuestion()]));

  update = (index, patch) => this.emit(this.list().map((q, i) => (i === index ? { ...q, ...patch } : q)));

  setType = (index, qtype) => {
    const q = this.list()[index];
    const keepsOptions = CHOICE_TYPES.includes(qtype);
    const patch = { qtype: qtype, options: keepsOptions ? (q.options || []) : [] };
    if (qtype === "table") {
      patch.columns = Array.isArray(q.columns) && q.columns.length ? q.columns : [newColumn([])];
      patch.max_rows = q.max_rows === undefined ? String(TABLE_DEFAULT_ROWS) : q.max_rows;
    }
    this.update(index, patch);
  };

  setOptions = (index, text) => {
    this.setState({ drafts: { ...this.state.drafts, [index]: text } });
    this.update(index, { options: parseOptions(text) });
  };

  // Reorder / remove shift the indexes, so the typed drafts are dropped (the parsed options stay).
  remove = (index) => this.emit(this.list().filter((_, i) => i !== index), {});

  move = (index, delta) => {
    const next = this.list().slice();
    const other = index + delta;
    if (other < 0 || other >= next.length) return;
    [next[index], next[other]] = [next[other], next[index]];
    this.emit(next, {});
  };

  // ---- table columns

  setColumns = (index, columns) => this.update(index, { columns: columns });

  addColumn = (index) => {
    const columns = this.list()[index].columns || [];
    this.setColumns(index, columns.concat([newColumn(columns.map((c) => c.key))]));
  };

  // A typed label re-makes that column's key, unique among the other columns.
  setColumnLabel = (index, j, label) => {
    const columns = this.list()[index].columns || [];
    const taken = columns.filter((_, k) => k !== j).map((c) => c.key);
    this.setColumns(index, columns.map((c, k) => (k === j ? { ...c, label: label, key: columnKey(label, taken) } : c)));
  };

  setColumnType = (index, j, type) => {
    const columns = this.list()[index].columns || [];
    this.setColumns(index, columns.map((c, k) => (k === j ? { ...c, type: type } : c)));
  };

  removeColumn = (index, j) => this.setColumns(index, (this.list()[index].columns || []).filter((_, k) => k !== j));

  renderTable = (q, index, id) => {
    const columns = q.columns || [];
    return (
      <div className="broadcast-table-editor">
        <h6>Columns</h6>
        {columns.map((c, j) => {
          const cid = id("col-" + j);
          return (
            <div className="form-row broadcast-table-column" key={j} data-testid={"question-" + index + "-column-" + j}>
              <div className="form-group col-md-5">
                <label htmlFor={cid + "-label"}>Column {j + 1} label</label>
                <input type="text" className="form-control" id={cid + "-label"} maxLength={100} value={c.label || ""}
                  onChange={(e) => this.setColumnLabel(index, j, e.target.value)} />
                <small className="form-text text-muted">Key: <code>{c.key}</code></small>
              </div>
              <div className="form-group col-md-4">
                <label htmlFor={cid + "-type"}>Column {j + 1} type</label>
                <select className="form-control" id={cid + "-type"} value={c.type || "text"} onChange={(e) => this.setColumnType(index, j, e.target.value)}>
                  {TABLE_COLUMN_TYPES.map((t) => <option key={t} value={t}>{COLUMN_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div className="form-group col-md-3 broadcast-table-column-remove">
                <Button size="sm" variant="outline-danger" type="button" aria-label={"Remove column " + (j + 1)}
                  disabled={columns.length <= 1} onClick={() => this.removeColumn(index, j)}>Remove column</Button>
              </div>
            </div>
          );
        })}
        <Button size="sm" variant="outline-primary" type="button" disabled={columns.length >= TABLE_MAX_COLUMNS} onClick={() => this.addColumn(index)}>Add column</Button>
        <div className="form-group mt-2">
          <label htmlFor={id("max-rows")}>Max rows</label>
          <input type="number" className="form-control" id={id("max-rows")} min={1} max={20} value={q.max_rows === undefined ? "" : q.max_rows}
            onChange={(e) => this.update(index, { max_rows: e.target.value })} />
          <small className="form-text text-muted">1 to 20 rows, 1 to 8 columns.</small>
        </div>
      </div>
    );
  };

  optionsText = (q, index) => (this.state.drafts[index] !== undefined ? this.state.drafts[index] : (q.options || []).join("\n"));

  renderQuestion = (q, index) => {
    const n = index + 1;
    const id = (field) => "question-" + index + "-" + field;
    const last = this.list().length - 1;
    return (
      <fieldset className="broadcast-question-editor" key={index}>
        <legend>Question {n}</legend>
        <div className="form-row">
          <div className="form-group col-md-3">
            <label htmlFor={id("type")}>Type</label>
            <select className="form-control" id={id("type")} value={q.qtype} onChange={(e) => this.setType(index, e.target.value)}>
              {QUESTION_TYPES.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
            </select>
          </div>
          <div className="form-group col-md-9">
            <label htmlFor={id("label")}>Label</label>
            <input type="text" className="form-control" id={id("label")} maxLength={500} value={q.label || ""}
              onChange={(e) => this.update(index, { label: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor={id("help")}>Help text</label>
          <input type="text" className="form-control" id={id("help")} maxLength={500} value={q.help_text || ""}
            onChange={(e) => this.update(index, { help_text: e.target.value })} />
        </div>
        {CHOICE_TYPES.includes(q.qtype) ? (
          <div className="form-group">
            <label htmlFor={id("options")}>Options (one per line)</label>
            <textarea className="form-control" id={id("options")} rows={4} value={this.optionsText(q, index)}
              onChange={(e) => this.setOptions(index, e.target.value)} />
            <small className="form-text text-muted">2 to 20 options, each up to 100 characters.</small>
          </div>
        ) : null}
        {q.qtype === "table" ? this.renderTable(q, index, id) : null}
        {this.props.perPerson ? (
          <div className="form-group">
            <label htmlFor={id("filled-by")}>Filled in by</label>
            <select className="form-control" id={id("filled-by")} value={q.filled_by === "hr" ? "hr" : "employee"}
              onChange={(e) => this.update(index, { filled_by: e.target.value })}>
              {FILLED_BY.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
            </select>
          </div>
        ) : null}
        <div className="form-check">
          <input type="checkbox" className="form-check-input" id={id("required")} checked={!!q.required}
            onChange={(e) => this.update(index, { required: e.target.checked })} />
          <label className="form-check-label" htmlFor={id("required")}>Required</label>
        </div>
        <div className="broadcast-question-actions">
          <Button size="sm" variant="outline-secondary" type="button" disabled={index === 0} onClick={() => this.move(index, -1)}>Move up</Button>
          <Button size="sm" variant="outline-secondary" type="button" disabled={index === last} onClick={() => this.move(index, 1)}>Move down</Button>
          <Button size="sm" variant="outline-danger" type="button" onClick={() => this.remove(index)}>Remove</Button>
        </div>
      </fieldset>
    );
  };

  render() {
    const list = this.list();
    const { hasResponses, version } = this.props;
    const current = Number(version) || 1;
    return (
      <div className="broadcast-questions">
        {hasResponses ? (
          <p className="alert alert-info">
            {"Editing questions will create version " + (current + 1) + "; earlier answers stay with version " + current}
          </p>
        ) : null}
        {list.length === 0 ? <p className="text-muted">No questions yet</p> : list.map(this.renderQuestion)}
        <Button variant="outline-primary" type="button" disabled={list.length >= MAX_QUESTIONS} onClick={this.add}>Add question</Button>
      </div>
    );
  }
}

export default QuestionBuilder;
